import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MessageCircle, X, Send, ChevronLeft, Inbox, Search } from "lucide-react";

/**
 * Gmail-like floating DM widget. Bottom-right FAB toggles an inbox panel
 * showing recent DM threads with unread counts. Click a thread to open a mini
 * chat view in-place. Persists to MongoDB via /workspace/dm/* endpoints.
 *
 * Mounted globally inside the authenticated layout (AppLayout / DashboardLayout).
 */
export default function DmInboxFab() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // thread.other_user
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("");
  const [directoryHits, setDirectoryHits] = useState([]);
  const [searching, setSearching] = useState(false);

  // Whenever the query is 2+ chars, look up matching users in the directory so
  // people can start a fresh conversation without needing a shared workspace.
  useEffect(() => {
    const q = filter.trim();
    if (q.length < 2) { setDirectoryHits([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/workspace/dm/search-users?q=${encodeURIComponent(q)}`);
        if (!cancelled) setDirectoryHits(r.data || []);
      } catch { if (!cancelled) setDirectoryHits([]); }
      finally { if (!cancelled) setSearching(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [filter]);

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/workspace/dm/unread-count");
      setUnread(r.data?.unread || 0);
    } catch { /* silent */ }
  }, [user]);

  const refreshThreads = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/workspace/dm/inbox");
      setThreads(r.data || []);
    } catch { /* silent */ }
  }, [user]);

  // Periodic polling for unread count (every 30s) so badge stays fresh.
  useEffect(() => {
    if (!user) return;
    refreshUnread();
    const t = setInterval(refreshUnread, 30000);
    return () => clearInterval(t);
  }, [user, refreshUnread]);

  // Allow any component to open the DM widget for a specific partner via
  //   window.dispatchEvent(new CustomEvent("kb-open-dm", { detail: { user } }))
  useEffect(() => {
    const onOpen = (e) => {
      const partner = e.detail?.user;
      if (!partner) return;
      setActive(partner);
      setOpen(true);
    };
    window.addEventListener("kb-open-dm", onOpen);
    return () => window.removeEventListener("kb-open-dm", onOpen);
  }, []);

  // Reload threads whenever the panel opens.
  useEffect(() => {
    if (open) refreshThreads();
  }, [open, refreshThreads]);

  if (!user) return null;

  const filtered = !filter.trim() ? threads : threads.filter((t) =>
    (t.other_user?.name || "").toLowerCase().includes(filter.toLowerCase()) ||
    (t.last_body || "").toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-[80] w-12 h-12 rounded-full bg-kb-primary text-white shadow-lg hover:shadow-xl flex items-center justify-center transition-transform hover:scale-105"
        title="Messages"
        data-testid="dm-fab"
      >
        <MessageCircle className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white kb-mono text-[10px] flex items-center justify-center" data-testid="dm-unread-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed bottom-20 right-5 z-[90] w-[360px] max-w-[calc(100vw-32px)] h-[520px] max-h-[calc(100vh-120px)] bg-white border border-slate-200 rounded-sm shadow-2xl flex flex-col"
          data-testid="dm-panel"
        >
          {active ? (
            <ThreadView
              partner={active}
              onBack={() => { setActive(null); refreshThreads(); refreshUnread(); }}
              onClose={() => setOpen(false)}
            />
          ) : (
            <InboxView
              threads={filtered}
              filter={filter}
              onFilter={setFilter}
              onPick={(t) => setActive(t.other_user)}
              onClose={() => setOpen(false)}
              directoryHits={directoryHits}
              searching={searching}
              onStartWith={(u) => setActive({ user_id: u.user_id, name: u.name, picture: u.picture, role: u.role })}
              existingThreadUserIds={threads.map((t) => t.other_user?.user_id).filter(Boolean)}
            />
          )}
        </div>
      )}
    </>
  );
}

function InboxView({ threads, filter, onFilter, onPick, onClose, directoryHits = [], searching = false, onStartWith, existingThreadUserIds = [] }) {
  const newPeople = (directoryHits || []).filter((u) => !existingThreadUserIds.includes(u.user_id));
  const showDirectorySection = filter.trim().length >= 2;
  return (
    <>
      <div className="flex items-center gap-2 px-4 py-3 bg-kb-ink text-white">
        <Inbox className="w-4 h-4" />
        <div className="flex-1 text-sm font-medium">Messages</div>
        <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="dm-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-2 border-b border-slate-200">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-kb-ink-light absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            className="kb-input text-xs w-full pl-8"
            placeholder="Search people or messages…"
            value={filter}
            onChange={(e) => onFilter(e.target.value)}
            data-testid="dm-search"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
        {threads.length === 0 && !showDirectorySection ? (
          <div className="text-center text-xs text-kb-ink-muted py-10 px-4">
            No messages yet. Search for a teammate above to start a conversation.
          </div>
        ) : threads.map((t) => (
          <button
            key={t.thread_key}
            type="button"
            onClick={() => onPick(t)}
            className={`w-full text-left flex gap-3 px-3 py-3 hover:bg-slate-50 transition-colors ${
              t.unread > 0 ? "bg-kb-primary/5" : ""
            }`}
            data-testid={`dm-thread-${t.other_user?.user_id}`}
          >
            {t.other_user?.picture ? (
              <img src={t.other_user.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-kb-primary/10 flex items-center justify-center kb-mono text-xs text-kb-primary shrink-0">
                {t.other_user?.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className={`text-sm truncate ${t.unread > 0 ? "font-medium text-kb-ink" : "text-kb-ink"}`}>
                  {t.other_user?.name || "Unknown"}
                </div>
                <div className="kb-mono text-[9px] text-kb-ink-light shrink-0">{fmtTime(t.last_at)}</div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <div className={`text-xs truncate ${t.unread > 0 ? "text-kb-ink" : "text-kb-ink-muted"}`}>
                  {t.last_from_me ? "You: " : ""}{t.last_body}
                </div>
                {t.unread > 0 && (
                  <span className="kb-mono text-[9px] bg-kb-primary text-white rounded-full px-1.5 min-w-[18px] h-[16px] flex items-center justify-center shrink-0">
                    {t.unread}
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
        {showDirectorySection && (
          <div className="py-2">
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted px-3 py-1.5 bg-slate-50 border-y border-slate-100">Start a new chat</div>
            {searching ? (
              <div className="text-center text-xs text-kb-ink-muted py-4">Searching…</div>
            ) : newPeople.length === 0 ? (
              <div className="text-center text-xs text-kb-ink-muted py-4 px-3">No teammates match &quot;{filter}&quot;.</div>
            ) : newPeople.map((u) => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => onStartWith?.(u)}
                className="w-full text-left flex gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors"
                data-testid={`dm-directory-${u.user_id}`}
              >
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-kb-primary/10 flex items-center justify-center kb-mono text-xs text-kb-primary shrink-0">
                    {u.name?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-kb-ink truncate">{u.name}</div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{u.role}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function ThreadView({ partner, onBack, onClose }) {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/dm/with/${partner.user_id}`);
      setMsgs(r.data || []);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch { /* silent */ }
  }, [partner.user_id]);

  useEffect(() => { load(); }, [load]);
  // Poll new messages every 10s while thread is open.
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const r = await api.post("/workspace/dm/send", { to_user_id: partner.user_id, body: body.trim() });
      setMsgs((m) => [...m, r.data]);
      setBody("");
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 30);
    } catch { /* silent */ } finally { setSending(false); }
  };

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-3 bg-kb-ink text-white">
        <button onClick={onBack} className="text-white/70 hover:text-white" data-testid="dm-thread-back">
          <ChevronLeft className="w-4 h-4" />
        </button>
        {partner.picture ? (
          <img src={partner.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center kb-mono text-[10px]">
            {partner.name?.[0]?.toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{partner.name}</div>
          {partner.role && <div className="kb-mono text-[9px] uppercase tracking-wider text-white/60">{partner.role}</div>}
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="dm-thread-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50/50">
        {msgs.length === 0 ? (
          <div className="text-center text-xs text-kb-ink-muted py-8">No messages yet. Say hi 👋</div>
        ) : msgs.map((m) => {
          const fromMe = m.from_user_id === user?.user_id;
          return (
            <div key={m.id} className={`flex ${fromMe ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] px-3 py-2 rounded-sm text-sm whitespace-pre-wrap break-words ${
                fromMe ? "bg-kb-primary text-white" : "bg-white border border-slate-200 text-kb-ink"
              }`}>
                {m.body}
                <div className={`kb-mono text-[9px] mt-1 ${fromMe ? "text-white/60" : "text-kb-ink-light"}`}>
                  {fmtTime(m.created_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t border-slate-200 p-2 flex gap-2">
        <input
          className="kb-input text-sm flex-1"
          placeholder={`Message ${partner.name?.split(" ")[0]}…`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
          data-testid="dm-input"
        />
        <button type="button" onClick={send} disabled={!body.trim() || sending} className="kb-btn-primary text-sm" data-testid="dm-send">
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffH = (now - d) / 3600000;
    if (diffH < 24) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (diffH < 24 * 7) return d.toLocaleDateString([], { weekday: "short" });
    return d.toLocaleDateString();
  } catch { return ""; }
}
